import React, { useMemo, useState, useRef } from 'react';
import { Tooltip, Modal } from 'antd';
import dayjs from 'dayjs';

import useTimelineState from './useTimelineState';
import TimelineGanttBar from './TimelineGanttBar';

import {
    TIMELINE_SECTIONS,
    VIEW_MODES,
    ROW_HEIGHT,
    SECTION_ROW_HEIGHT,
} from './constants';
import {
    groupTasksByStatus,
    computeTodayOffset,
    deriveInsights,
    getFullName,
    getPhotoUrl,
    getInitials,
    buildBarPositionMap,
    computeDependencyLines,
} from './timelineUtils';
// Note: dayjs plugins (isoWeek, quarterOfYear, isBetween, weekOfYear) are
// extended once inside timelineUtils.js — no need to re-extend here.

// ─────────────────────────────────────────────────────────────────────────────
// MiniAvatar — sidebar list only
// ─────────────────────────────────────────────────────────────────────────────
function MiniAvatar({ assignee }) {
    const [imgErr, setImgErr] = useState(false);
    if (!assignee) {
        return (
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-400 flex-shrink-0 overflow-hidden">?</div>
        );
    }
    const name = getFullName(assignee);
    const photoUrl = getPhotoUrl(assignee?.photo);
    const show = photoUrl && !imgErr;
    return (
        <Tooltip title={name || 'Assignee'}>
            <div
                className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden ring-2 ring-white flex items-center justify-center text-sm font-bold text-white shadow-sm"
                style={show ? {} : { background: 'linear-gradient(90deg,#03488A 0%,#00AAF1 100%)' }}
            >
                {show
                    ? <img src={photoUrl} alt={name} className="w-full h-full object-cover rounded-full" onError={() => setImgErr(true)} />
                    : getInitials(name)
                }
            </div>
        </Tooltip>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Priority dot
// ─────────────────────────────────────────────────────────────────────────────
function PriorityDot({ priority = 'Medium' }) {
    const colors = { High: 'bg-red-500', Critical: 'bg-red-600', Medium: 'bg-amber-500', Low: 'bg-green-500' };
    return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors[priority] || colors.Medium}`} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// InsightTasksModal — task list for a specific insight alert
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_DOT_COLOR = {
    Backlog: 'bg-slate-300',
    'In Progress': 'bg-[#28b5fd]',
    'Under Review': 'bg-amber-400',
    Completed: 'bg-emerald-400',
    'On Hold': 'bg-orange-400',
    Cancelled: 'bg-red-400',
};

const PRIORITY_BADGE_STYLE = {
    Critical: 'bg-[#ffdad6] text-[#93000a]',
    High: 'bg-red-50 text-red-600',
    Medium: 'bg-[#c9e6ff] text-[#004c6e]',
    Low: 'bg-[#e7e8e9] text-[#424750]',
};

function InsightTasksModal({ insight, open, onClose, onTaskClick }) {
    if (!insight) return null;

    const typeHeader = {
        error: 'bg-red-50 border-red-200 text-red-800',
        warning: 'bg-amber-50 border-amber-200 text-amber-800',
        info: 'bg-blue-50 border-blue-200 text-blue-800',
    };
    const headerStyle = typeHeader[insight.type] || typeHeader.info;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={640}
            centered
            closable={false}
            destroyOnClose
            styles={{
                content: { padding: 0, borderRadius: 16, overflow: 'hidden' },
                body: { padding: 0 },
            }}
        >
            <div className="flex flex-col">
                {/* Header */}
                <div className={`px-6 py-4 border-b flex items-start justify-between gap-4 ${headerStyle}`}>
                    <div>
                        <h3 className="text-base font-bold font-headline">{insight.title}</h3>
                        <p className="text-xs mt-0.5 opacity-80">{insight.message}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-black/10 transition-colors flex-shrink-0"
                        aria-label="Close"
                    >
                        <span className="material-symbols-outlined text-lg" style={{ fontFamily: 'Material Symbols Outlined' }}>close</span>
                    </button>
                </div>

                {/* Task list */}
                <div className="max-h-[60vh] overflow-y-auto divide-y divide-[#f3f4f5] dark:divide-slate-700">
                    {(insight.tasks || []).length === 0 ? (
                        <p className="px-6 py-8 text-sm text-center text-[#737782]">No tasks found.</p>
                    ) : (
                        (insight.tasks || []).map((task) => {
                            const overdue = task.dueDate && !['Completed', 'Cancelled'].includes(task.status)
                                && dayjs(task.dueDate).isBefore(dayjs(), 'day');
                            const assigneeName = getFullName(task.assignee);
                            return (
                                <div
                                    key={task._id}
                                    onClick={() => { onTaskClick(task); onClose(); }}
                                    className="flex items-center gap-4 px-6 py-3.5 hover:bg-[#f8f9fa] dark:hover:bg-[#1e293b]/60 cursor-pointer transition-colors"
                                >
                                    {/* Status dot */}
                                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT_COLOR[task.status] || 'bg-slate-300'}`} />

                                    {/* Title + project */}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-[#191c1d] dark:text-white truncate">
                                            {task.title}
                                        </p>
                                        {task.project?.name && (
                                            <p className="text-sm text-[#737782] dark:text-slate-400 truncate">
                                                {task.project.name}
                                            </p>
                                        )}
                                    </div>

                                    {/* Assignee */}
                                    {assigneeName && (
                                        <span className="text-sm text-[#424750] dark:text-slate-400 flex-shrink-0 hidden sm:block">
                                            {assigneeName}
                                        </span>
                                    )}

                                    {/* Priority badge */}
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${PRIORITY_BADGE_STYLE[task.priority] || PRIORITY_BADGE_STYLE.Medium}`}>
                                        {task.priority}
                                    </span>

                                    {/* Due date */}
                                    {task.dueDate && (
                                        <span className={`text-sm font-semibold flex-shrink-0 ${overdue ? 'text-red-500' : 'text-[#424750] dark:text-slate-400'}`}>
                                            {dayjs(task.dueDate).format('D MMM')}
                                        </span>
                                    )}

                                    {/* Open arrow */}
                                    <span className="material-symbols-outlined text-[#c2c6d2] text-base flex-shrink-0" style={{ fontFamily: 'Material Symbols Outlined' }}>
                                        chevron_right
                                    </span>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-[#e7e8e9] dark:border-slate-700 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 text-sm font-semibold text-[#424750] dark:text-slate-300 border border-[#c2c6d2]/30 dark:border-slate-600 hover:bg-[#e7e8e9] dark:hover:bg-[#334155] rounded-lg transition-all"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Timeline Insights side panel
// ─────────────────────────────────────────────────────────────────────────────
function TimelineInsights({ tasks, onTaskClick }) {
    const insights = useMemo(() => deriveInsights(tasks), [tasks]);
    const [selectedInsight, setSelectedInsight] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const typeStyles = {
        error: { wrap: 'bg-red-300/10 border border-red-300/20 hover:bg-red-300/20', icon: 'text-red-500' },
        warning: { wrap: 'bg-amber-300/10 border border-amber-300/20 hover:bg-amber-300/20', icon: 'text-amber-500' },
        info: { wrap: 'bg-blue-100/50 border border-blue-200 hover:bg-blue-100', icon: 'text-blue-500' },
    };

    const iconMap = {
        warning: 'warning',
        schedule: 'schedule',
        balance: 'balance',
        event_upcoming: 'event_upcoming',
    };

    const handleClick = (ins) => {
        if (!ins.tasks?.length) return;
        setSelectedInsight(ins);
        setModalOpen(true);
    };

    return (
        <>
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl border border-[#e7e8e9] dark:border-slate-700 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-4">
                    <span className="material-symbols-outlined text-[#003263] dark:text-[#a7c8ff]" style={{ fontVariationSettings: "'FILL' 1", fontSize: 18 }}>insights</span>
                    <h3 className="text-sm font-bold text-[#003263] dark:text-[#a7c8ff] font-headline">Timeline Insights</h3>
                </div>
                <div className="space-y-2">
                    {insights.length === 0 ? (
                        <p className="text-sm text-[#737782] italic">No alerts at this time.</p>
                    ) : (
                        insights.map((ins, i) => {
                            const s = typeStyles[ins.type] || typeStyles.info;
                            const hasTasks = (ins.tasks?.length || 0) > 0;
                            return (
                                <div
                                    key={i}
                                    onClick={() => handleClick(ins)}
                                    className={`flex items-start gap-2.5 p-3 rounded-xl transition-all duration-150 ${s.wrap} ${hasTasks ? 'cursor-pointer active:scale-[0.99]' : 'cursor-default'}`}
                                >
                                    <span
                                        className={`material-symbols-outlined flex-shrink-0 mt-0.5 ${s.icon}`}
                                        style={{ fontFamily: 'Material Symbols Outlined', fontSize: 16 }}
                                    >
                                        {iconMap[ins.icon] || 'info'}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-[#191c1d] dark:text-white leading-tight">
                                            {ins.title}
                                        </p>
                                        <p className="text-xs text-[#737782] dark:text-slate-400 mt-0.5 leading-snug">
                                            {ins.message}
                                        </p>
                                    </div>
                                    {hasTasks && (
                                        <span
                                            className="material-symbols-outlined text-[#c2c6d2] flex-shrink-0 self-center"
                                            style={{ fontFamily: 'Material Symbols Outlined', fontSize: 16 }}
                                        >
                                            chevron_right
                                        </span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            <InsightTasksModal
                insight={selectedInsight}
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onTaskClick={onTaskClick}
            />
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading skeleton
// ─────────────────────────────────────────────────────────────────────────────
function TimelineSkeleton() {
    return (
        <div className="flex flex-col gap-4 animate-pulse">
            {/* Row 1: Pipeline card + Gantt card */}
            <div className="flex gap-4">
                <div className="w-64 flex-shrink-0 bg-white dark:bg-[#1e293b] rounded-2xl border border-[#e7e8e9] dark:border-slate-700 shadow-sm p-4 space-y-2" style={{ minHeight: 400 }}>
                    <div className="h-5 bg-slate-100 rounded w-32 mb-4" />
                    {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-9 bg-slate-100 rounded" />)}
                </div>
                <div className="flex-1 bg-white dark:bg-[#1e293b] rounded-2xl border border-[#e7e8e9] dark:border-slate-700 shadow-sm p-4 space-y-2">
                    <div className="h-9 bg-slate-100 rounded mb-3" />
                    {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-10 bg-slate-50 rounded" />)}
                </div>
            </div>
            {/* Row 2: Insights card */}
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl border border-[#e7e8e9] dark:border-slate-700 shadow-sm p-4 space-y-3">
                <div className="h-5 bg-slate-100 rounded w-32 mb-2" />
                {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-14 bg-slate-100 rounded" />)}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section badge helpers
// ─────────────────────────────────────────────────────────────────────────────
function getSectionStyles(key) {
    switch (key) {
        case 'IN_PROGRESS': return {
            rowClass: 'flex items-center justify-between bg-[#03488A]/5 border-l-4 border-[#03488A] cursor-pointer px-3',
            badgeClass: 'text-xs font-bold bg-[#03488A] text-white px-1.5 rounded',
            titleClass: 'text-xs font-bold text-[#03488A]',
        };
        case 'UNDER_REVIEW': return {
            rowClass: 'flex items-center justify-between hover:bg-[#f3f4f5] dark:hover:bg-slate-700/40 cursor-pointer px-3 transition-colors',
            badgeClass: 'text-xs font-bold bg-amber-300/20 text-amber-600 px-1.5 rounded',
            titleClass: 'text-xs font-bold text-[#191c1d] dark:text-white',
        };
        case 'COMPLETED': return {
            rowClass: 'flex items-center justify-between hover:bg-[#f3f4f5] dark:hover:bg-slate-700/40 cursor-pointer px-3 transition-colors',
            badgeClass: 'text-xs font-bold bg-emerald-50 text-emerald-600 px-1.5 rounded',
            titleClass: 'text-xs font-bold text-[#191c1d] dark:text-white',
        };
        case 'ON_HOLD': return {
            rowClass: 'flex items-center justify-between hover:bg-[#f3f4f5] dark:hover:bg-slate-700/40 cursor-pointer px-3 transition-colors',
            badgeClass: 'text-xs font-bold bg-orange-100 text-orange-600 px-1.5 rounded',
            titleClass: 'text-xs font-bold text-[#191c1d] dark:text-white',
        };
        case 'CANCELED': return {
            rowClass: 'flex items-center justify-between hover:bg-[#f3f4f5] dark:hover:bg-slate-700/40 cursor-pointer px-3 transition-colors',
            badgeClass: 'text-xs font-bold bg-red-100 text-red-600 px-1.5 rounded',
            titleClass: 'text-xs font-bold text-[#191c1d] dark:text-white',
        };
        default: return {
            rowClass: 'flex items-center justify-between hover:bg-[#f3f4f5] dark:hover:bg-slate-700/40 cursor-pointer px-3 transition-colors',
            badgeClass: 'text-xs font-bold bg-slate-100 text-slate-600 px-1.5 rounded',
            titleClass: 'text-xs font-bold text-[#191c1d] dark:text-white',
        };
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Main TimelineBoard component
// ─────────────────────────────────────────────────────────────────────────────
export default function TimelineBoard({ tasks = [], loading = false, onTaskClick }) {
    const {
        viewMode,
        columns,
        gridStart,
        gridEnd,
        cellWidth,
        gridWidthPx,
        goPrev,
        goNext,
        goToday,
        changeViewMode,
        toggleSection,
        isSectionCollapsed,
    } = useTimelineState();

    const SECTION_KEYS = TIMELINE_SECTIONS.map((s) => s.key);

    // Use real tasks only — mock tasks removed so the timeline
    // accurately reflects the grid window when navigating forward/back.
    const processedTasks = useMemo(() => tasks, [tasks]);

    const grouped = useMemo(() => groupTasksByStatus(processedTasks, SECTION_KEYS), [processedTasks]);

    // Build bar position map for dependency line rendering
    const barPositionMap = useMemo(
        () => buildBarPositionMap(processedTasks, gridStart, gridEnd, gridWidthPx, ROW_HEIGHT, SECTION_ROW_HEIGHT, SECTION_KEYS, grouped, isSectionCollapsed),
        [processedTasks, gridStart, gridEnd, gridWidthPx, grouped, isSectionCollapsed]
    );

    const dependencyLines = useMemo(
        () => computeDependencyLines(processedTasks, barPositionMap),
        [processedTasks, barPositionMap]
    );

    const todayPx = useMemo(
        () => computeTodayOffset(gridStart, gridEnd, gridWidthPx),
        [gridStart, gridEnd, gridWidthPx]
    );

    // Board height — based on actual row count
    const totalContentHeight = TIMELINE_SECTIONS.reduce((acc, section) => {
        const sectionTasks = grouped.get(section.key) || [];
        const collapsed = isSectionCollapsed(section.key);
        return acc + SECTION_ROW_HEIGHT + (collapsed ? 0 : Math.max(sectionTasks.length, 1) * ROW_HEIGHT);
    }, 0);
    const boardHeight = Math.min(Math.max(totalContentHeight + SECTION_ROW_HEIGHT + 60, 520), 650); // Cap at 650px to enable internal scroll

    // Scroll sync refs
    const sidebarRef = useRef(null);
    const ganttRef = useRef(null);

    const handleSidebarScroll = (e) => {
        if (ganttRef.current && ganttRef.current.scrollTop !== e.target.scrollTop) {
            ganttRef.current.scrollTop = e.target.scrollTop;
        }
    };

    const handleGanttScroll = (e) => {
        if (sidebarRef.current && sidebarRef.current.scrollTop !== e.target.scrollTop) {
            sidebarRef.current.scrollTop = e.target.scrollTop;
        }
    };

    if (loading) return <TimelineSkeleton />;

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-col lg:flex-row gap-4 w-full items-start overflow-x-hidden">

                {/* ── UNIFIED PIPELINE + GANTT BOX ── */}
                <div className="flex-1 min-w-0 bg-white dark:bg-[#1e293b] rounded-2xl border border-[#e7e8e9] dark:border-slate-700 shadow-sm flex overflow-hidden" style={{ height: boardHeight }}>

                    {/* PIPELINE SECTIONS (Left Sidebar) */}
                    <div data-tour="timeline-sidebar-pipeline" className="w-72 border-r border-[#e7e8e9] dark:border-slate-700 bg-[#f8f9fa] dark:bg-[#0f172a] flex-shrink-0 flex flex-col">
                        <div className="p-4 flex items-center border-b border-[#e7e8e9] dark:border-slate-700 flex-shrink-0 font-bold text-xs text-[#737782] dark:text-slate-400 uppercase tracking-widest" style={{ height: SECTION_ROW_HEIGHT + 4 }}>
                            Pipeline Sections
                        </div>
                        <div className="divide-y divide-[#e7e8e9] dark:divide-slate-700 flex-1 overflow-y-auto custom-scrollbar" ref={sidebarRef} onScroll={handleSidebarScroll}>
                            {TIMELINE_SECTIONS.map((section) => {
                                const sectionTasks = grouped.get(section.key) || [];
                                const collapsed = isSectionCollapsed(section.key);
                                return (
                                    <div key={section.key}>
                                        <div
                                            className="px-3 flex items-center justify-between cursor-pointer hover:bg-[#e7e8e9] dark:hover:bg-slate-700 transition-colors"
                                            style={{ height: SECTION_ROW_HEIGHT, borderLeft: !collapsed ? `3px solid ${section.accentColor}` : '3px solid transparent' }}
                                            onClick={() => toggleSection(section.key)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-sm transition-transform duration-200" style={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', fontFamily: 'Material Symbols Outlined' }}>chevron_right</span>
                                                <span className="text-xs font-bold text-[#191c1d] dark:text-white">{section.label}</span>
                                            </div>
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded min-w-[22px] text-center ${section.badgeBg} ${section.badgeText}`}>
                                                {String(sectionTasks.length).padStart(2, '0')}
                                            </span>
                                        </div>
                                        {!collapsed && sectionTasks.map(task => (
                                            <div
                                                key={task._id}
                                                className="px-4 flex items-center gap-3 border-b border-gray-100 dark:border-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer transition-colors group"
                                                style={{ height: ROW_HEIGHT }}
                                                onClick={() => onTaskClick?.(task)}
                                            >
                                                <MiniAvatar assignee={task.assignee} />
                                                <div className="flex-1 min-w-0">
                                                    <p className="truncate text-xs font-semibold text-[#191c1d] dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300">{task.title}</p>
                                                    {task.project?.name && (
                                                        <p className="truncate text-sm text-[#737782] dark:text-slate-400">{task.project.name}</p>
                                                    )}
                                                </div>
                                                <PriorityDot priority={task.priority} />
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* GANTT BOARD (Center Panel) */}
                    <div className="flex-1 flex flex-col overflow-hidden relative">

                        {/* Date column headers */}
                        <div className="flex border-b border-[#e7e8e9] dark:border-slate-700 bg-[#f8f9fa]/40 dark:bg-[#0f172a]/30 flex-shrink-0 overflow-x-hidden" style={{ minWidth: '100%' }}>
                            <div className="flex w-full" style={{ minWidth: gridWidthPx }}>
                                {columns.map((col) => (
                                    <div
                                        key={col.key}
                                        className={`flex-1 flex flex-col items-center justify-center ${col.isToday ? 'bg-[#03488A]/5 dark:bg-[#a7c8ff]/5' : ''}`}
                                        style={{ minWidth: cellWidth, height: SECTION_ROW_HEIGHT + 4 }}
                                    >
                                        <span className={`text-xs font-bold leading-none ${col.isToday ? 'text-[#03488A] dark:text-[#a7c8ff]' : 'text-[#737782] dark:text-slate-400'}`}>{col.label}</span>
                                        {col.isToday && <span className="text-xs font-bold text-[#03488A] dark:text-[#a7c8ff] uppercase tracking-widest mt-0.5">Today</span>}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Gantt scrollable body */}
                        <div data-tour="timeline-gantt-bars" className="flex-1 overflow-x-auto overflow-y-auto pb-14 relative custom-scrollbar" ref={ganttRef} onScroll={handleGanttScroll}>
                            <div className="relative w-full" style={{ minWidth: gridWidthPx, minHeight: '100%' }}>

                                {todayPx !== null && (
                                    <div className="absolute top-0 bottom-0 z-20 pointer-events-none flex flex-col items-center" style={{ left: todayPx, width: 1, backgroundColor: 'rgba(3,72,138,0.3)' }}>
                                        <div className="bg-[#03488A] text-white text-[8px] font-bold px-1.5 py-0.5 rounded-b whitespace-nowrap leading-none mt-2">TODAY</div>
                                    </div>
                                )}

                                {dependencyLines.length > 0 && (
                                    <svg data-tour="timeline-dependencies" className="absolute top-0 left-0" style={{ width: gridWidthPx, height: '100%', zIndex: 5, pointerEvents: 'none' }} aria-hidden="true">
                                        <defs>
                                            <marker id="arrow-normal" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0 L0,6 L8,3 z" fill="#475569" /></marker>
                                            <marker id="arrow-risk" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0 L0,6 L8,3 z" fill="#ef4444" /></marker>
                                        </defs>
                                        {dependencyLines.map((line, i) => {
                                            const fromDate = line.fromDue ? dayjs(line.fromDue).format('D MMM') : null;
                                            const toDate = line.toStart ? dayjs(line.toStart).format('D MMM') : null;
                                            const tooltipLines = [
                                                `${line.fromTitle} → ${line.toTitle}`,
                                                `Dependency: ${line.typeLabel || 'Finish → Start'}`,
                                                fromDate ? `Blocker due: ${fromDate}` : null,
                                                toDate ? `This task starts: ${toDate}` : null,
                                                line.isAtRisk ? '⚠ At Risk: starts before blocker finishes' : null,
                                            ].filter(Boolean).join('\n');
                                            return (
                                                <g key={i} style={{ pointerEvents: 'stroke' }}>
                                                    <path d={line.d} fill="none" stroke="transparent" strokeWidth={12} style={{ pointerEvents: 'stroke', cursor: 'default' }}><title>{tooltipLines}</title></path>
                                                    <path d={line.d} fill="none" stroke={line.isAtRisk ? '#ef4444' : '#475569'} strokeWidth={line.isAtRisk ? 2 : 1.5} strokeDasharray={line.isAtRisk ? '0' : '6 3'} markerEnd={line.isAtRisk ? 'url(#arrow-risk)' : 'url(#arrow-normal)'} opacity={1} style={{ pointerEvents: 'none' }} />
                                                </g>
                                            );
                                        })}
                                    </svg>
                                )}

                                {TIMELINE_SECTIONS.map((section) => {
                                    const sectionTasks = grouped.get(section.key) || [];
                                    const collapsed = isSectionCollapsed(section.key);
                                    return (
                                        <div key={section.key}>
                                            <div className="border-b border-[#e7e8e9] dark:border-slate-700" style={{ height: SECTION_ROW_HEIGHT }} />
                                            {!collapsed && (
                                                sectionTasks.length > 0
                                                    ? sectionTasks.map((task) => (
                                                        <div key={task._id} className="relative hover:bg-[#f0f7ff] dark:hover:bg-[#1e3a5f]/20 transition-colors" style={{ height: ROW_HEIGHT }}>
                                                            <TimelineGanttBar task={task} section={section} gridStart={gridStart} gridEnd={gridEnd} gridWidthPx={gridWidthPx} onTaskClick={onTaskClick} />
                                                        </div>
                                                    ))
                                                    : <div style={{ height: ROW_HEIGHT }} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Floating controls */}
                        <div data-tour="timeline-view-modes" className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-white/95 dark:bg-[#1e293b]/95 backdrop-blur-sm px-3 py-2 rounded-xl border border-[#e7e8e9] dark:border-slate-700 shadow-lg">
                            <div className="flex bg-[#f3f4f5] dark:bg-slate-700 p-1 rounded-lg">
                                {VIEW_MODES.map((mode) => (
                                    <button key={mode.key} onClick={() => changeViewMode(mode.key)} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${viewMode === mode.key ? 'bg-white dark:bg-[#334155] text-[#003263] dark:text-[#a7c8ff] shadow-sm' : 'text-[#737782] dark:text-slate-400 hover:text-[#003263] dark:hover:text-white'}`}>
                                        {mode.label}
                                    </button>
                                ))}
                            </div>
                            <div className="w-px h-4 bg-[#e7e8e9] dark:bg-slate-600 mx-1" />
                            <div data-tour="timeline-navigation" className="flex items-center gap-1">
                                <button onClick={goPrev} className="p-1 rounded-lg hover:bg-[#f3f4f5] dark:hover:bg-slate-700 transition-all text-[#424750] dark:text-slate-400" aria-label="Previous">
                                    <span className="material-symbols-outlined" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 16 }}>chevron_left</span>
                                </button>
                                <button onClick={goToday} className="px-3 py-1 text-xs font-bold bg-white dark:bg-[#334155] border border-[#e7e8e9] dark:border-slate-600 rounded-lg hover:bg-[#f3f4f5] dark:hover:bg-slate-700 transition-all text-[#003263] dark:text-[#a7c8ff]">Today</button>
                                <button onClick={goNext} className="p-1 rounded-lg hover:bg-[#f3f4f5] dark:hover:bg-slate-700 transition-all text-[#424750] dark:text-slate-400" aria-label="Next">
                                    <span className="material-symbols-outlined" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 16 }}>chevron_right</span>
                                </button>
                            </div>
                        </div>
                    </div>

                </div>

                {/* ── INSIGHTS PANEL (Right) ── */}
                <div data-tour="timeline-insights-panel" className="w-full lg:w-96 flex-shrink-0">
                    <TimelineInsights tasks={processedTasks} onTaskClick={onTaskClick} />
                </div>

            </div>
        </div>
    );
}
